// Global, app-level meeting call state. Lives ABOVE the router outlet (mounted
// inside the persistent Layout shell) so an active call survives page
// navigation — the user can move around Strata while staying on the call.
//
// The actual call UI (video + controls) is rendered ONCE by <ActiveCallLayer/>
// reading this context; nothing here touches the Daily object, so navigating
// between pages never tears the call down.

import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const MeetingCallContext = createContext(null);

export const useMeetingCall = () => {
  const ctx = useContext(MeetingCallContext);
  if (!ctx) throw new Error('useMeetingCall must be used within a MeetingCallProvider');
  return ctx;
};

export function MeetingCallProvider({ children }) {
  // session: { room_url, token, title, session_id, guest, role } | null
  const [session, setSession] = useState(null);
  const [minimized, setMinimized] = useState(false);

  const join = useCallback((info) => {
    if (!info?.room_url) return;
    setSession(info);
    setMinimized(false);
  }, []);

  const leave = useCallback(() => {
    setSession(null);
    setMinimized(false);
  }, []);

  const value = useMemo(() => ({
    session,
    minimized,
    inCall: !!session,
    join,
    leave,
    minimize: () => setMinimized(true),
    maximize: () => setMinimized(false),
    toggleMinimize: () => setMinimized((v) => !v),
  }), [session, minimized, join, leave]);

  return (
    <MeetingCallContext.Provider value={value}>
      {children}
    </MeetingCallContext.Provider>
  );
}
